import { FISCAL_ONBOARDING_COPY, FISCAL_ONBOARDING_ROUTE, FISCAL_STATUS_PILLS } from '../constants/fiscal-onboarding.constants';
import { FiscalHomePill, FiscalOnboardingState, FiscalOnboardingStatus, FiscalPageView, FiscalStatusPill } from '../models';

/** Vista de la página Fiscal para un estado (sin expediente ⇒ landing). */
export function viewForStatus(status: FiscalOnboardingStatus | null | undefined): FiscalPageView {
    switch (status) {
        case 'borrador':
            return 'resume';
        case 'en_revision':
        case 'en_activacion':
        case 'error':
            return 'review';
        case 'requiere_correccion':
            return 'corrections';
        case 'pendiente_firma':
            return 'sign';
        case 'activo':
            return 'active';
        default:
            return 'landing';
    }
}

/** Sin expediente pero ya factura con UniDigital (integración externa): se trata como activo, sin perfil (ADM-834). */
export function isActiveWithoutOnboarding(state: FiscalOnboardingState | null | undefined): boolean {
    return !!state && (!state.exists || !state.status) && state.integrationActive === true;
}

/** Vista de la página Fiscal para el estado completo: la del status, salvo el comercio que ya factura sin expediente (Activo). */
export function viewForState(state: FiscalOnboardingState | null | undefined): FiscalPageView {
    return isActiveWithoutOnboarding(state) ? 'active' : viewForStatus(state?.status);
}

/** Píldora de estado de la pantalla; `null` sin expediente (landing). */
export function pillForStatus(status: FiscalOnboardingStatus | null | undefined): FiscalStatusPill | null {
    if (!status) return null;
    const pill = FISCAL_STATUS_PILLS[status];
    return pill ? { ...pill } : null;
}

/**
 * Píldora de la cabecera del Home: invita a empezar sin expediente; con trámite en curso (borrador incluido)
 * dice «Activación fiscal» con el chip del estado, como el prototipo, y desaparece al activar. Solo la ve el
 * dueño (lo decide quien la pinta). Tampoco aparece si ya factura con UniDigital sin expediente (ADM-834).
 */
export function homePillFor(state: FiscalOnboardingState | null | undefined): FiscalHomePill | null {
    if (!state) return null;
    if (isActiveWithoutOnboarding(state)) return null;
    const link = FISCAL_ONBOARDING_ROUTE;
    if (!state.exists || !state.status) return { label: FISCAL_ONBOARDING_COPY.homePill.start, tone: 'info', chip: null, link };
    if (state.status === 'activo') return null;
    const pill = FISCAL_STATUS_PILLS[state.status];
    return pill ? { label: FISCAL_ONBOARDING_COPY.homePill.inProgress, tone: pill.tone, chip: pill.label, link } : null;
}

/** Badge del ítem «Fiscal» del menú: solo mientras hay un trámite en curso (nunca en landing ni activo). */
export function menuBadgeFor(state: FiscalOnboardingState | null | undefined): FiscalStatusPill | null {
    if (!state?.exists || !state.status || state.status === 'activo') return null;
    const pill = FISCAL_STATUS_PILLS[state.status];
    return pill ? { ...pill } : null;
}

/** «12 nov 2026» en español (zona del navegador). */
export function formatShortDate(iso: string | null | undefined): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (!Number.isFinite(date.getTime())) return '';
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}
